(ns dre.pages.pubmed-compare.views
  (:require
   ["antd" :refer [Spin Alert Row Col Divider]]
   [clojure.set :as set]
   [dre.coll :refer [somev]]
   [dre.components.collapse :refer [collapse]]
   [dre.components.icon :refer [fa-icon]]
   [dre.components.list :refer [collapsable-list]]
   [dre.components.pill :as pill]
   [dre.i18n.views :refer [tr]]
   [dre.pages.pubmed-compare.events :as events]
   [dre.pages.search.events :as search-events]
   [dre.shared.interface.search.pills :as pills]
   [dre.shared.interface.search.blunt :as blunt]
   [dre.utils.bem :as bem]
   [re-frame.core :as rf]
   [reagent.core :as r]))

(def component-name :pubmed-compare)

(defn main-content
  [query]
  [:div
   {:class (bem/be component-name :panel)}
   [collapse
    [{:label (r/as-element [:b [tr "Compare with PubMed"]])
      :children (r/as-element [:<>
                               [:p
                                "This page allows you to compare results from the current search query "
                                "with PubMed results."]
                               [:p [:b "Pitfalls and limitations:"]]
                               [:div
                                [:ul
                                 [:li
                                  "PubMed API has a limitation of 5000 maximum results per query. "
                                  "More generic queries that return many results will thus be harder to compare"]
                                 [:li
                                  "Do not use date pills. PubMed has a very different logic when "
                                  "limiting articles within a specific date, making comparison impossible "]
                                 [:li
                                  "When searching for multi-word terms, e.g. 'eye cancer', pubmed will return "
                                  "articles that contain words 'eye' and 'cancer' anywhere in text. DocSearch "
                                  "only returns results that contain these words together"]
                                 [:li
                                  "Some sections like affiliations or journal titles are opt in in DocSearch, "
                                  "but PubMed searches in those sections by default."]]]
                               (if (seq (:query/pills query))
                                 [:<>
                                  [:button
                                   {:class [(bem/bm :button :brand)]
                                    :on-click #(rf/dispatch [::events/compare query])}
                                   [tr "Compare with PubMed"]]]
                                 [:p [:b "Search query is empty, please perform regular search first."]])])}]
    :all]])

(defn not-valid-content
  []
  [:div
   {:class (bem/be component-name :panel)}
   [:> Alert {:type "error"
              :message "Could not create a a valid pubmed query."}]])

(defn compare-table
  [results]
  (let [r results
        pidss (set (:pubmed/ids r))
        sidss (set (:search/ids r))
        cids (sort > (set/intersection pidss sidss))
        pids (sort > (set/difference pidss sidss))
        sids (sort > (set/difference sidss pidss))
        hyperf (fn [[pmid guid]]
                 [:span
                  [:a {:href (str "/details/" guid)} pmid]
                  " "
                  [:a
                   {:href (str "https://pubmed.ncbi.nlm.nih.gov/" pmid)
                    :style {:width "20px" :height "20px" :display "table-cell"}}
                   [fa-icon :external-link]]])
        item-link (fn [items]
                    (let [coll (map hyperf items)
                          coll (partition-all 10 coll)
                          coll (map #(interpose [:span ", "] %) coll)
                          coll (map #(vec (cons :span %)) coll)]
                      coll))
        pill (pills/normalize (pills/coerce (:query/pill r)))
        blunt-pill (blunt/parse (:query/blunt r))
        pills (somev [pill blunt-pill {:pill/type :category
                                       :pill/query "medline"
                                       :pill/label "Pubmed"}])]
    [:div
   {:class (bem/be component-name :panel)}
   [collapse
    [{:label (r/as-element [:b [tr "Compare Results"]])
      :children (r/as-element [:<>
                               [:> Row
                                {:gutter 16}
                                [:> Col
                                 {:span 4}
                                 [:b [tr "PubMed Query"]]]
                                [:> Col
                                 {:span 20}
                                 (:pubmed/query r)]]
                               [:> Divider]
                               [:> Row
                                {:gutter 16}
                                [:> Col
                                 {:span 4}
                                 [:b [tr "PubMed Count"]]]
                                [:> Col
                                 {:span 20}
                                 (:pubmed/count r)]]
                               [:> Divider]
                               [:> Row
                                {:gutter 16}
                                [:> Col
                                 {:span 4}
                                 [:b [tr "DocSearch Query"]]]
                                [:> Col
                                 {:span 20}
                                 [pill/readonly-pills pills nil nil nil {}]]]
                               [:> Divider]
                               [:> Row
                                {:gutter 16}
                                [:> Col
                                 {:span 4}
                                 [:b [tr "DocSearch Count"]]]
                                [:> Col
                                 {:span 20}
                                 (:article/count r)]]
                               [:> Divider]
                               [:> Row
                                {:gutter 16}
                                [:> Col
                                 {:span 4}
                                 [:b "Found in both (" (str (count cids)) ")"]]
                                [:> Col
                                 {:span 20}
                                 [collapsable-list
                                  (mapv (fn [item]
                                          {:content (r/as-element item)}) (item-link cids))
                                  15]]]
                               [:> Divider]
                               [:> Row
                                {:gutter 16}
                                [:> Col
                                 {:span 4}
                                 [:b "Found in PubMed only (" (str (count pids)) ")"]]
                                [:> Col
                                 {:span 20}
                                 [collapsable-list
                                  (mapv (fn [item]
                                          {:content (r/as-element item)}) (item-link pids))
                                  15]]]
                               [:> Divider]
                               [:> Row
                                {:gutter 16}
                                [:> Col
                                 {:span 4}
                                 [:b "Found in DocSearch only (" (str (count sids)) ")"]]
                                [:> Col
                                 {:span 20}
                                 [collapsable-list
                                  (mapv (fn [item]
                                          {:content (r/as-element item)}) (item-link sids))
                                  15]]]])}]
    :all]]))

(defn view
  []
  (let [loading? @(rf/subscribe [::events/loading?])
        results @(rf/subscribe [::events/data])
        results-valid? (or (not (contains? results :pubmed/valid?))
                           (:pubmed/valid? results))
        search-query @(rf/subscribe [::search-events/search-query])]
    [:div
     {:class component-name}
     (if loading?
       [:> Spin]
       [:<>
        [main-content search-query]
        (when (not results-valid?)
          [not-valid-content])
        (when results
          [compare-table results])])]))
